import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { cockpitTokens } from './cockpitTokens';

/**
 * The line the cockpit opens on (DX-74).
 *
 * It replaces a header, a headline and a paragraph that between them named the
 * portal three times before the page said anything — roughly 700px of a first
 * screen spent introducing a page the reader had already chosen to open. What
 * is left is one line that says who is reading and one that says what the page
 * is for. The bar above the page carries the sections, so Catalog and Docs are
 * not repeated here; and the one action that stood beside the greeting,
 * creating a component, went with DX-546 — the portal's GitHub App cannot
 * create a repository yet (DX-238), and a button that leads to a run that
 * stops is not an action. It comes back with the write access. The line of
 * build counts under the greeting went the same way: it said what the builds
 * say, which the attention card already says, and DX-536 decides what a line
 * there should be about.
 *
 * The greeting is deliberately not a heading: `portal-shell.md` AC-1 puts the
 * product name in the bar above, and a second `<h1>` under it would restate
 * the same level of the document. It is a `<p>`, and the page's first heading
 * stays with the cards that carry content.
 *
 * MUI v4 only.
 */

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    flexWrap: 'wrap' as const,
  },
  greeting: {
    margin: 0,
    fontSize: '1.6rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: cockpitTokens.color.text,
  },
  sub: {
    margin: `${theme.spacing(0.5)}px 0 0`,
    fontSize: '0.9rem',
    color: cockpitTokens.color.textMuted,
  },
}));

/**
 * The name a colleague is greeted by.
 *
 * The first word of the display name: "Welcome back, Elmar Hepp" reads
 * like a form letter, and the surname carries nothing the reader does not
 * know. A profile with no display name falls back to a greeting with no name
 * at all rather than to the email local-part, which would put an account
 * identifier where a name belongs.
 */
const firstNameOf = (displayName?: string) =>
  displayName?.trim().split(/\s+/)[0] ?? undefined;

/**
 * The greeting for the reader's time of day, as Michael's draft opens (DX-307):
 * the page is read on the way to something else, and a line that knows it is
 * morning reads as written for this visit rather than for every visit.
 */
export const greetingFor = (hour: number) => {
  if (hour < 5) return 'Good evening';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

export const CockpitWelcome = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const [firstName, setFirstName] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    identityApi
      .getProfileInfo()
      .then(profile => {
        if (!cancelled) {
          setFirstName(firstNameOf(profile.displayName));
        }
      })
      .catch(() => {
        // A missing profile is not worth an error on the page's first line:
        // the greeting simply arrives without a name, and everything below it
        // is unaffected.
      });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  return (
    <div className={classes.root}>
      <div>
        <p className={classes.greeting}>
          {greetingFor(new Date().getHours())}
          {firstName ? `, ${firstName}.` : '.'}
        </p>
        <p className={classes.sub}>
          Here's what needs your attention — and where you left off.
        </p>
      </div>
      {/*
        No action beside the greeting. Catalog and Docs stood here until the
        bar above the page gained the portal's sections — the same destination
        on one screen twice, which is what AC-18 is about — and "Create a
        service" until DX-546, for the reason the header comment gives.
      */}
    </div>
  );
};
